import {
  Button,
  Controller,
  ErrorMessage,
  Select,
  useCustomForm,
  useEffect,
} from "@";
import { useDriversQuery } from "@/features/operations/verifications/drivers/api/queries";
import { useAssignDriverMutation } from "../api/mutations";
import {
  assignDriverDefaultValues,
  assignDriverSchema,
} from "../lib/validation.ts";
import type {
  AssignDriverFormValues,
  AssignDriverRequest,
  DonationsSearchValues,
} from "../types.ts";

interface AssignDriverModalProps {
  ids: number[];
  searchValues?: DonationsSearchValues;
  selectingAll: boolean;
}

const AssignDriverModal = ({
  ids,
  searchValues,
  selectingAll,
}: AssignDriverModalProps) => {
  const { watch, errors, control, setValue, handleSubmit, isSubmitting } =
    useCustomForm(assignDriverDefaultValues, assignDriverSchema);

  // Fetch drivers using React Query
  const { data: driversData } = useDriversQuery();
  const drivers = driversData?.drivers || [];

  // Mutation
  const assignDriverMutation = useAssignDriverMutation();

  // Map drivers to options for the select input
  const driversOptions = drivers.map((driver) => ({
    value: driver.id,
    label: driver.name,
  }));

  // Submit the form to assign a driver
  const submit = async (form: AssignDriverFormValues) => {
    await assignDriverMutation.mutateAsync({
      driverId: form.driverId.toString(),
      verificationStages: form.verificationStages,
      searchValues: form.searchValues,
      selectingAll: form.selectingAll,
    } as AssignDriverRequest);
  };

  // Update verification stages when ids change
  useEffect(() => {
    setValue("verificationStages", ids);
    setValue("searchValues", searchValues);
    setValue("selectingAll", selectingAll);
  }, [ids, setValue, searchValues, selectingAll]);

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="flex min-h-44 flex-col justify-center gap-3 p-2 md:p-5"
    >
      {ids?.length === 0 && (
        <ErrorMessage className="mb-5" value="رجاء تحديد طلب واحد على الاقل" />
      )}

      <Controller
        name="driverId"
        control={control}
        render={({ field }) => (
          <Select
            isRtl
            {...field}
            isClearable
            isSearchable
            className="lard-select mb-2"
            placeholder="اختر مندوب"
            options={driversOptions}
            isDisabled={ids?.length === 0}
            value={driversOptions.find(
              (option) => option.value === parseInt(watch("driverId") || "0"),
            )}
            onChange={(option: { value: number } | null) =>
              field.onChange(option?.value?.toString() || "")
            }
          />
        )}
      />
      <ErrorMessage value={errors.driverId?.message} />

      {ids?.length > 0 && (
        <Button
          className="w-full py-3"
          isLoading={isSubmitting}
          type="submit"
          title="إسناد للمندوب"
        />
      )}
    </form>
  );
};

export default AssignDriverModal;
